/**
 * Inspector mount for routes that skip client hydration (homepage, careers pages ,
 * see LayoutDefault.tsx and StaticPageLayout.tsx). These routes still run the full
 * React tree through Vike's SSR renderer (just never call hydrateRoot), so a Context
 * Provider wrapping them server-side takes effect in the emitted HTML, markers from
 * `<Msg>` get baked directly into the static HTML with no client JS required for the
 * visual outlining. Usage recording and the hover popover (copy keys, English original,
 * "Show English") need a small inline script, since those normally run from a `useEffect`
 * that never fires here.
 *
 * `enabled` is computed server-side in vike-handler.ts (`canReviewTranslations`) and
 * is never available to flip client-side on these routes, so there is no working
 * toggle button here (unlike InspectorOverlay.tsx), turning the inspector on/off
 * happens once, from the backoffice testing page, and is picked up on next load.
 *
 * Off-by-default / zero-cost: renders children untouched when `enabled` is false.
 */
import React from 'react';
import { OVERLAY_STYLE } from '../client/InspectorOverlay';
import { buttonClass } from '../../design-system/ui/Button/Button';
import { ClipboardReadout, InspectorKeysHint, InspectorPanelFrame } from './InspectorPanel';
import { StaticInspectorProvider } from '../client/inspector';
import {
  STATIC_PANEL_CLIPBOARD_ID,
  STATIC_PANEL_ENGLISH_ID,
  buildStaticInspectorScript,
} from './build-static-inspector-script';

export function StaticInspectorMount({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  if (!enabled) return <>{children}</>;

  return (
    <StaticInspectorProvider enabled>
      {children}
      <style>{OVERLAY_STYLE}</style>
      <InspectorPanelFrame>
        <p className="type-caption text-jc-ink-secondary">
          On. Turn it off under Translator tools in the menu.
        </p>
        {/* Wired up by the inline script below (hidden there on English pages). Not a Switch: the
            script can only flip attributes, so the pressed look comes from aria-pressed. */}
        <button
          type="button"
          id={STATIC_PANEL_ENGLISH_ID}
          aria-pressed="false"
          className={`${buttonClass('secondary', 'sm')} aria-pressed:bg-jc-selected aria-pressed:text-jc-on-selected aria-pressed:ring-0`}
        >
          Show English
        </button>
        <InspectorKeysHint />
        <ClipboardReadout text={null} id={STATIC_PANEL_CLIPBOARD_ID} />
      </InspectorPanelFrame>
      {/* Must come after the marked-up content above, inline scripts run synchronously
          as the HTML streams, same ordering requirement as ResourceCookieConsentBanner. */}
      <script dangerouslySetInnerHTML={{ __html: buildStaticInspectorScript() }} />
    </StaticInspectorProvider>
  );
}
